import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
  orderBy,
  writeBatch,
  QueryConstraint,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { normalizePhoneNumber } from "@/lib/constants";
import { Lead, Connector, SMContact, getLegacyCustomerName } from "@/types";
import { startOfDay, endOfDay, startOfMonth, endOfMonth } from "date-fns";

export interface AdminStats {
  totalLoginToday: number;
  loginAmount: number;
  totalDisbursementToday: number;
  disbursedAmount: number;
  totalVendorVisits: number;
  totalLeadsStatusUpdated: number;
  totalLeadsInPipeline: number;
  totalNewLeads: number;
  totalNewVendorsAdded: number;
  // Targets from commitments
  loginTargetFiles?: number;
  loginTargetAmount?: number;
  disbursementTargetFiles?: number;
  disbursementTargetAmount?: number;
}

export interface DateRangeFilter {
  startDate: Date;
  endDate: Date;
}

export interface StatsAccessContext {
  userId?: string;
  userPhone?: string;
  officeId?: string;
  role?: string;
  roles?: string[];
}

function hasPrivilegedStatsAccess(accessContext?: StatsAccessContext): boolean {
  const privilegedRoles = [
    "admin",
    "super_admin",
    "business_head",
    "operations_user",
  ];
  const allRoles = [
    ...(accessContext?.role ? [accessContext.role] : []),
    ...(accessContext?.roles || []),
  ];
  return allRoles.some((role) => privilegedRoles.includes(role));
}

async function resolveUserIdFromPhone(
  teamMemberPhone?: string,
  accessContext?: StatsAccessContext,
): Promise<string | undefined> {
  if (!teamMemberPhone) {
    return undefined;
  }

  const normalizedPhone = normalizePhoneNumber(teamMemberPhone);

  // Under least-privilege rules, non-privileged users can only resolve themselves.
  if (!hasPrivilegedStatsAccess(accessContext)) {
    const normalizedUserPhone = accessContext?.userPhone
      ? normalizePhoneNumber(accessContext.userPhone)
      : undefined;

    if (
      normalizedUserPhone &&
      normalizedPhone === normalizedUserPhone &&
      accessContext.userId
    ) {
      return accessContext.userId;
    }
    return undefined;
  }

  const approvedUsersRef = collection(db, "approvedUsers");

  // Primary lookup against normalized phone field.
  const phoneSnapshot = await getDocs(
    query(approvedUsersRef, where("phone", "==", normalizedPhone)),
  );
  if (!phoneSnapshot.empty) {
    const matchedUser = phoneSnapshot.docs[0];
    const userData = matchedUser.data();
    return userData.userId || matchedUser.id;
  }

  // Legacy fallback for older records using phoneNumber.
  const legacyPhoneSnapshot = await getDocs(
    query(approvedUsersRef, where("phoneNumber", "==", normalizedPhone)),
  );
  if (!legacyPhoneSnapshot.empty) {
    const matchedUser = legacyPhoneSnapshot.docs[0];
    const userData = matchedUser.data();
    return userData.userId || matchedUser.id;
  }

  return undefined;
}

/**
 * Get admin statistics for a date range
 */
export async function getAdminStats(
  dateRange: DateRangeFilter,
  teamMemberPhone?: string,
  accessContext?: StatsAccessContext,
): Promise<AdminStats> {
  const { startDate, endDate } = dateRange;

  try {
    const teamMemberUserId = await resolveUserIdFromPhone(
      teamMemberPhone,
      accessContext,
    );
    const isPrivileged = hasPrivilegedStatsAccess(accessContext);
    const currentUserId = accessContext?.userId;

    const leadsRef = collection(db, "leads");
    const fetchOperationalLeads = async (): Promise<Lead[]> => {
      if (!currentUserId) return [];

      // With restrictive rules, operational users can reliably read own/shared data only.
      if (teamMemberUserId && teamMemberUserId !== currentUserId) {
        return [];
      }

      const leadMap = new Map<string, Lead>();

      const ownSnapshot = await getDocs(
        query(leadsRef, where("createdBy", "==", currentUserId)),
      );
      ownSnapshot.docs.forEach((leadDoc) => {
        leadMap.set(leadDoc.id, {
          id: leadDoc.id,
          ...leadDoc.data(),
        } as Lead);
      });

      const assignedSnapshot = await getDocs(
        query(leadsRef, where("assignedTo", "==", currentUserId)),
      );
      assignedSnapshot.docs.forEach((leadDoc) => {
        leadMap.set(leadDoc.id, {
          id: leadDoc.id,
          ...leadDoc.data(),
        } as Lead);
      });

      try {
        const sharedSnapshot = await getDocs(
          query(leadsRef, where("sharedWith", "array-contains", currentUserId)),
        );
        sharedSnapshot.docs.forEach((leadDoc) => {
          leadMap.set(leadDoc.id, {
            id: leadDoc.id,
            ...leadDoc.data(),
          } as Lead);
        });
      } catch (error) {
        console.warn(
          "Shared leads query failed, continuing with own leads",
          error,
        );
      }

      return Array.from(leadMap.values());
    };

    const fetchPrivilegedLeads = async (): Promise<Lead[]> => {
      const leadConstraints: QueryConstraint[] = [];
      if (teamMemberUserId) {
        leadConstraints.push(where("createdBy", "==", teamMemberUserId));
      }

      const leadsQuery =
        leadConstraints.length > 0
          ? query(leadsRef, ...leadConstraints)
          : query(leadsRef);
      const leadsSnapshot = await getDocs(leadsQuery);
      return leadsSnapshot.docs.map((leadDoc) => ({
        id: leadDoc.id,
        ...leadDoc.data(),
      })) as Lead[];
    };

    const leads = isPrivileged
      ? await fetchPrivilegedLeads()
      : await fetchOperationalLeads();

    // Helper: check if lead belongs to selected team member
    // Prefer createdBy (owner) — owner gets credit for all stats
    const matchesTeamMember = (lead: Lead) => {
      if (!teamMemberUserId) return true;
      const ownerId = (lead as any).createdBy || lead.assignedTo;
      return ownerId === teamMemberUserId;
    };

    // LEADS Added: Filter by createdAt in date range (leads CREATED in period)
    const filteredLeadsByCreation = leads.filter((lead) => {
      const createdAt =
        lead.createdAt instanceof Timestamp
          ? lead.createdAt.toDate()
          : new Date(lead.createdAt);
      return (
        createdAt >= startDate &&
        createdAt <= endDate &&
        matchesTeamMember(lead)
      );
    });
    const totalNewLeads = filteredLeadsByCreation.length;

    // Normalize a Firestore Timestamp | Date | string | number into a JS Date.
    const toJsDate = (value: unknown): Date | null => {
      if (value === null || value === undefined) return null;
      if (value instanceof Timestamp) return value.toDate();
      if (value instanceof Date) return value;
      const parsed = new Date(value as string | number);
      return isNaN(parsed.getTime()) ? null : parsed;
    };

    const inRange = (date: Date | null) =>
      !!date && date >= startDate && date <= endDate;

    // LOGIN: count EACH login filing (loginRecords[]) whose filing date
    // (createdAt) falls in the period — one customer can be logged in to several
    // banks, and every such application counts as a login. Dating by the login
    // record (not updatedAt) means a lead that later advances to "disbursed"
    // still counts in the period it was logged in, and disbursing does NOT create
    // a phantom login today. Legacy leads with no login records fall back to
    // updatedAt only while still sitting at the "logged_in" stage.
    let totalLoginToday = 0;
    let loginAmount = 0;
    leads.forEach((lead) => {
      if (!matchesTeamMember(lead)) return;

      const records = lead.loginRecords;
      if (records && records.length > 0) {
        records.forEach((r) => {
          if (inRange(toJsDate(r.createdAt))) {
            totalLoginToday++;
            loginAmount += r.loanAmount || 0;
          }
        });
        return;
      }

      // Legacy fallback: no login records and still at the login stage.
      if (lead.status === "logged_in" && inRange(toJsDate(lead.updatedAt))) {
        totalLoginToday++;
        loginAmount += lead.loanAmount || 0;
      }
    });

    // DISBURSED: count by sales consideration date, falling back to the
    // disbursement date when the consideration date is null (business rule).
    // One file per lead; the amount sums only the disbursements inside the period.
    let totalDisbursementToday = 0;
    let disbursedAmount = 0;
    leads.forEach((lead) => {
      if (!matchesTeamMember(lead)) return;

      const records = lead.disbursements;
      if (records && records.length > 0) {
        let leadAmountInPeriod = 0;
        let hasInPeriod = false;
        records.forEach((d) => {
          const effectiveDate =
            toJsDate(d.salesConsiderationDate) ?? toJsDate(d.disbursementDate);
          if (inRange(effectiveDate)) {
            leadAmountInPeriod += d.disbursedAmount || 0;
            hasInPeriod = true;
          }
        });
        if (hasInPeriod) {
          totalDisbursementToday++;
          disbursedAmount += leadAmountInPeriod;
        }
        return;
      }

      // Legacy fallback: disbursed lead without disbursement records — no
      // per-record disbursementDate exists, so use updatedAt as the last resort.
      if (lead.status === "disbursed" || lead.status === "partial_disbursed") {
        const effectiveDate =
          toJsDate(lead.salesConsiderationDate) ?? toJsDate(lead.updatedAt);
        if (inRange(effectiveDate)) {
          totalDisbursementToday++;
          disbursedAmount +=
            (lead as any).totalDisbursed ||
            lead.disbursedAmount ||
            lead.loanAmount ||
            0;
        }
      }
    });

    // STATUS UPDATES: Count leads IN THE PIPELINE with ANY activity today
    // Only count leads whose current status is NOT in the excluded list
    // This tracks "pipeline leads worked on today" for productivity monitoring
    const today = new Date();
    const todayStart = startOfDay(today);
    const todayEnd = endOfDay(today);

    // Pipeline exclusion list - must match the one below
    const excludedFromPipeline: string[] = [
      "partial_disbursed",
      "disbursed",
      "otc",
      "payout_released",
      "closed",
      "removed",
      "followup_later",
    ];

    let totalLeadsStatusUpdated = 0;

    try {
      // Get leads to check (filtered by team member if specified, AND in pipeline only)
      const leadsToCheckForActivity = leads.filter(
        (lead) =>
          matchesTeamMember(lead) &&
          !excludedFromPipeline.includes(lead.status),
      );

      // Count leads with timeline activity today
      const leadsUpdatedToday = new Set<string>();
      leadsToCheckForActivity.forEach((lead) => {
        if (!lead.timeline || lead.timeline.length === 0) return;

        // Check if lead has a "Today's Update" (isDailyUpdate) timeline entry created today
        // This corresponds to the green dot shown on lead cards
        const hasActivityToday = lead.timeline.some((item) => {
          if (!item.isDailyUpdate) return false;
          const itemDate =
            item.createdAt instanceof Timestamp
              ? item.createdAt.toDate()
              : new Date(item.createdAt);
          return itemDate >= todayStart && itemDate <= todayEnd;
        });

        if (hasActivityToday) {
          leadsUpdatedToday.add(lead.id);
        }
      });

      totalLeadsStatusUpdated = leadsUpdatedToday.size;
    } catch (err) {
      console.warn("Could not count lead activity:", err);
    }

    // Get leads to check for pipeline count (filter by team member if specified)
    const leadsToCheck = leads.filter((lead) => matchesTeamMember(lead));

    // PIPELINE: Match Leads page "All" tab exactly
    // Uses same excludedFromPipeline list declared above
    const totalLeadsInPipeline = leadsToCheck.filter(
      (l) => !excludedFromPipeline.includes(l.status),
    ).length;

    // Get vendor visits (from channelPartners collection - same as channelPartnerService.ts uses)
    const connectorsRef = collection(db, "channelPartners");

    const fetchOperationalConnectors = async () => {
      if (!currentUserId) return [] as Array<{ id: string; data: any }>;

      // With restrictive rules, operational users can reliably read own/shared data only.
      if (teamMemberUserId && teamMemberUserId !== currentUserId) {
        return [] as Array<{ id: string; data: any }>;
      }

      const connectorMap = new Map<string, { id: string; data: any }>();

      const ownSnapshot = await getDocs(
        query(connectorsRef, where("createdBy", "==", currentUserId)),
      );
      ownSnapshot.docs.forEach((connectorDoc) => {
        connectorMap.set(connectorDoc.id, {
          id: connectorDoc.id,
          data: connectorDoc.data(),
        });
      });

      try {
        const sharedSnapshot = await getDocs(
          query(
            connectorsRef,
            where("sharedWith", "array-contains", currentUserId),
          ),
        );
        sharedSnapshot.docs.forEach((connectorDoc) => {
          connectorMap.set(connectorDoc.id, {
            id: connectorDoc.id,
            data: connectorDoc.data(),
          });
        });
      } catch (error) {
        console.warn(
          "Shared channel partners query failed, continuing with own data",
          error,
        );
      }

      return Array.from(connectorMap.values());
    };

    const fetchPrivilegedConnectors = async () => {
      const connectorConstraints: QueryConstraint[] = [];
      if (teamMemberUserId) {
        connectorConstraints.push(where("createdBy", "==", teamMemberUserId));
      }

      const connectorsSnapshot =
        connectorConstraints.length > 0
          ? await getDocs(query(connectorsRef, ...connectorConstraints))
          : await getDocs(connectorsRef);

      return connectorsSnapshot.docs.map((connectorDoc) => ({
        id: connectorDoc.id,
        data: connectorDoc.data(),
      }));
    };

    const connectors = isPrivileged
      ? await fetchPrivilegedConnectors()
      : await fetchOperationalConnectors();

    let vendorVisits = 0;
    let newVendorsAdded = 0;

    connectors.forEach((connector) => {
      const data = connector.data as any; // Type assertion to bypass cache issue

      // Count vendor visits
      if (data.lastVisitDate) {
        const lastVisit =
          data.lastVisitDate instanceof Timestamp
            ? data.lastVisitDate.toDate()
            : new Date(data.lastVisitDate);
        if (lastVisit >= startDate && lastVisit <= endDate) {
          // Check team member filter if provided (connectors store userId in lastVisitedBy)
          if (!teamMemberUserId || data.lastVisitedBy === teamMemberUserId) {
            vendorVisits++;
          }
        }
      }

      // Count new vendors added
      if (data.createdAt) {
        const createdAt =
          data.createdAt instanceof Timestamp
            ? data.createdAt.toDate()
            : new Date(data.createdAt);
        if (createdAt >= startDate && createdAt <= endDate) {
          // Check team member filter if provided (connectors store userId in createdBy)
          if (!teamMemberUserId || data.createdBy === teamMemberUserId) {
            newVendorsAdded++;
          }
        }
      }
    });

    return {
      totalLoginToday,
      loginAmount,
      totalDisbursementToday,
      disbursedAmount,
      totalVendorVisits: vendorVisits,
      totalLeadsStatusUpdated,
      totalLeadsInPipeline,
      totalNewLeads,
      totalNewVendorsAdded: newVendorsAdded,
    };
  } catch (error) {
    console.error("Error fetching admin stats:", error);
    throw error;
  }
}

/**
 * Get disbursement data with filters
 */
export async function getDisbursementData(filters: {
  startDate?: Date;
  endDate?: Date;
  filterType: "consideration" | "disbursement";
}) {
  const { startDate, endDate, filterType } = filters;

  const leadsRef = collection(db, "leads");
  const q = query(leadsRef, where("status", "==", "disbursed"));

  const snapshot = await getDocs(q);
  let disbursements = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Lead[];

  // Filter by date
  if (startDate && endDate) {
    disbursements = disbursements.filter((lead) => {
      const dateToCheck =
        filterType === "consideration"
          ? lead.salesConsiderationDate
            ? lead.salesConsiderationDate instanceof Timestamp
              ? lead.salesConsiderationDate.toDate()
              : new Date(lead.salesConsiderationDate)
            : null
          : lead.updatedAt
            ? lead.updatedAt instanceof Timestamp
              ? lead.updatedAt.toDate()
              : new Date(lead.updatedAt)
            : null;

      return dateToCheck && dateToCheck >= startDate && dateToCheck <= endDate;
    });
  }

  return disbursements;
}

/**
 * Bulk import vendors from Excel data
 */
export async function bulkImportVendors(vendors: Partial<Connector>[]) {
  const batch = writeBatch(db);
  const connectorsRef = collection(db, "connectors");

  vendors.forEach((vendor) => {
    const newDocRef = doc(connectorsRef);
    batch.set(newDocRef, {
      ...vendor,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
  });

  await batch.commit();
}

/**
 * Bulk import bankers from Excel data
 */
export async function bulkImportBankers(bankers: Partial<SMContact>[]) {
  const batch = writeBatch(db);
  const bankersRef = collection(db, "bankers");

  bankers.forEach((banker) => {
    const newDocRef = doc(bankersRef);
    batch.set(newDocRef, {
      ...banker,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
  });

  await batch.commit();
}

/**
 * Export disbursement data to Excel format (returns data array)
 */
export function prepareDisbursementExport(disbursements: Lead[]) {
  return disbursements.map((lead) => ({
    "Lead ID": lead.id,
    "Customer Name": getLegacyCustomerName(lead),
    Mobile: lead.mobile,
    "Loan Amount": lead.loanAmount,
    "Disbursed Amount": lead.disbursedAmount,
    Lender: lead.lender,
    Status: lead.status,
    "Consideration Date": lead.salesConsiderationDate
      ? lead.salesConsiderationDate instanceof Timestamp
        ? lead.salesConsiderationDate.toDate().toLocaleDateString()
        : new Date(lead.salesConsiderationDate).toLocaleDateString()
      : "",
    "Disbursement Date": lead.updatedAt
      ? lead.updatedAt instanceof Timestamp
        ? lead.updatedAt.toDate().toLocaleDateString()
        : new Date(lead.updatedAt).toLocaleDateString()
      : "",
    "Created By": lead.createdBy,
    "Assigned To": lead.assignedTo,
  }));
}

/**
 * Get commitment targets for admin stats.
 * @param period - 'today' for daily commitments, 'month' for monthly commitments.
 * @param teamMemberPhone - Optional team member phone filter; resolved to a userId.
 * @param accessContext - Current user context used for role-based access checks.
 *
 * Behavior:
 * - Privileged roles can query all users when no team member filter is provided.
 * - Privileged roles can filter to a specific user by passing teamMemberPhone.
 * - Non-privileged roles are restricted to their own commitments, even without a team filter.
 */
export async function getCommitmentTargets(
  period: "today" | "month",
  teamMemberPhone?: string,
  accessContext?: StatsAccessContext,
): Promise<{
  loginTargetFiles: number;
  loginTargetAmount: number;
  disbursementTargetFiles: number;
  disbursementTargetAmount: number;
}> {
  try {
    const now = new Date();
    const isPrivileged = hasPrivilegedStatsAccess(accessContext);
    const teamMemberUserId = await resolveUserIdFromPhone(
      teamMemberPhone,
      accessContext,
    );
    const userIdForQuery =
      teamMemberUserId || (!isPrivileged ? accessContext?.userId : undefined);
    const collectionName =
      period === "today" ? "dailyCommitments" : "monthlyCommitments";

    let q;
    if (userIdForQuery) {
      // Get specific user's commitment
      if (period === "today") {
        const dateStr = now.toISOString().split("T")[0]; // YYYY-MM-DD
        q = query(
          collection(db, collectionName),
          where("userId", "==", userIdForQuery),
          where("date", "==", dateStr),
        );
      } else {
        const monthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`; // YYYY-MM
        q = query(
          collection(db, collectionName),
          where("userId", "==", userIdForQuery),
          where("month", "==", monthStr),
        );
      }
    } else {
      // Get all users' commitments
      if (period === "today") {
        const dateStr = now.toISOString().split("T")[0];
        q = query(collection(db, collectionName), where("date", "==", dateStr));
      } else {
        const monthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
        q = query(
          collection(db, collectionName),
          where("month", "==", monthStr),
        );
      }
    }

    const snapshot = await getDocs(q);

    // Aggregate targets
    let loginTargetFiles = 0;
    let loginTargetAmount = 0;
    let disbursementTargetFiles = 0;
    let disbursementTargetAmount = 0;

    snapshot.docs.forEach((doc) => {
      const data = doc.data() as any;
      loginTargetFiles += data.loginFiles || 0;
      loginTargetAmount += data.loginAmount || 0;
      disbursementTargetFiles += data.disbursementFiles || 0;
      disbursementTargetAmount += data.disbursementAmount || 0;
    });

    return {
      loginTargetFiles,
      loginTargetAmount,
      disbursementTargetFiles,
      disbursementTargetAmount,
    };
  } catch (error) {
    console.error("Error fetching commitment targets:", error);
    return {
      loginTargetFiles: 0,
      loginTargetAmount: 0,
      disbursementTargetFiles: 0,
      disbursementTargetAmount: 0,
    };
  }
}
